import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Rating,
  CircularProgress,
  Alert,
  Divider,
  Stack
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  ResponsiveContainer
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { workOrdersApi } from '../../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`analytics-tabpanel-${index}`}
    aria-labelledby={`analytics-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const WorkOrderAnalyticsDashboard = () => {
  const { user, canAccess } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [timeRange, setTimeRange] = useState('30');
  
  // Analytics data
  const [analytics, setAnalytics] = useState(null);
  const [contractorPerformance, setContractorPerformance] = useState([]);
  const [defectSummary, setDefectSummary] = useState([]);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    if (canAccess(['manager'])) {
      fetchAnalyticsData();
    }
  }, [timeRange, canAccess]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');

      const [analyticsRes, performanceRes, defectRes, timelineRes] = await Promise.all([
        workOrdersApi.getDetailedAnalytics({ timeRange }),
        workOrdersApi.getContractorPerformance(),
        workOrdersApi.getDefectSummary({ timeRange }),
        workOrdersApi.getTimeline({ limit: 20 })
      ]);

      setAnalytics(analyticsRes.data);
      setContractorPerformance(performanceRes.data);
      setDefectSummary(defectRes.data);
      setTimeline(timelineRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAnalyticsData();
  };

  // Check access after all hooks are defined
  if (!canAccess(['manager'])) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Only managers can access work order analytics.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={40} sx={{ mr: 2 }} />
        <Typography variant="body1">Loading analytics...</Typography>
      </Box>
    );
  }

  const renderOverviewCards = () => {
    if (!analytics) return null;

    const cards = [
      {
        title: 'Total Work Orders',
        value: analytics.total_work_orders || 0,
        icon: <AssignmentIcon />,
        color: 'primary',
        subtitle: `${analytics.time_range_days} days`
      },
      {
        title: 'Completion Rate',
        value: `${analytics.completion_rate || 0}%`,
        icon: <CheckCircleIcon />,
        color: 'success',
        subtitle: `${analytics.completed_count} completed`
      },
      {
        title: 'Avg Completion Time',
        value: analytics.avg_completion_days ? `${analytics.avg_completion_days.toFixed(1)} days` : 'N/A',
        icon: <SpeedIcon />,
        color: 'info',
        subtitle: 'Average duration'
      },
      {
        title: 'Quality Rating',
        value: analytics.avg_quality_rating ? analytics.avg_quality_rating.toFixed(1) : 'N/A',
        icon: <StarIcon />,
        color: 'warning',
        subtitle: `${analytics.rated_work_orders} rated`
      },
      {
        title: 'Overdue Items',
        value: analytics.overdue_count || 0,
        icon: <WarningIcon />,
        color: 'error',
        subtitle: 'Need attention'
      }
    ];

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={2.4} key={index}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 2, color: `${card.color}.main` }}>
                  {card.icon}
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  {card.value}
                </Typography>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  {card.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderStatusDistribution = () => {
    if (!analytics) return null;

    const data = [
      { name: 'Pending', value: parseInt(analytics.pending_count) || 0, color: COLORS[0] },
      { name: 'Assigned', value: parseInt(analytics.assigned_count) || 0, color: COLORS[1] },
      { name: 'In Progress', value: parseInt(analytics.in_progress_count) || 0, color: COLORS[2] },
      { name: 'Completed', value: parseInt(analytics.completed_count) || 0, color: COLORS[3] },
      { name: 'Cancelled', value: parseInt(analytics.cancelled_count) || 0, color: COLORS[4] }
    ];

    // Only filter out if ALL values are 0
    const hasData = data.some(item => item.value > 0);
    const chartData = hasData ? data.filter(item => item.value > 0) : data;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Work Order Status Distribution
          </Typography>
          {hasData ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No work order data available</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderPriorityDistribution = () => {
    if (!analytics) return null;

    const data = [
      { name: 'Critical', value: parseInt(analytics.critical_count) || 0, color: '#FF4444' },
      { name: 'High', value: parseInt(analytics.high_count) || 0, color: '#FF8800' },
      { name: 'Normal', value: parseInt(analytics.normal_count) || 0, color: '#00C49F' },
      { name: 'Low', value: parseInt(analytics.low_count) || 0, color: '#0088FE' }
    ];

    // Only filter out if ALL values are 0
    const hasData = data.some(item => item.value > 0);
    const chartData = hasData ? data.filter(item => item.value > 0) : data;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Priority Distribution
          </Typography>
          {hasData ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8">
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No priority data available</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderTrendsChart = () => {
    if (!analytics || !analytics.trends || analytics.trends.length === 0) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Work Order Trends (Last {analytics.time_range_days} Days)
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="created_count" stroke="#8884d8" name="Created" />
              <Line type="monotone" dataKey="completed_count" stroke="#82ca9d" name="Completed" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  const renderTopPerformers = () => {
    if (!analytics || !analytics.top_performers || analytics.top_performers.length === 0) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Performers
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="center">Work Orders</TableCell>
                  <TableCell align="center">Completed</TableCell>
                  <TableCell align="center">Avg Rating</TableCell>
                  <TableCell align="center">Avg Days</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.top_performers.map((performer, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {performer.assignment_type === 'external' ? <BusinessIcon /> : <PersonIcon />}
                        </Avatar>
                        {performer.performer_name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={performer.assignment_type} 
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">{performer.work_orders_count}</TableCell>
                    <TableCell align="center">{performer.completed_count}</TableCell>
                    <TableCell align="center">
                      {performer.avg_rating ? (
                        <Rating value={performer.avg_rating} readOnly size="small" />
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {performer.avg_completion_days ? performer.avg_completion_days.toFixed(1) : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderDefectTypeSummary = () => {
    if (!defectSummary || defectSummary.length === 0) return null;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Work Orders by Defect Type
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Defect Type</TableCell>
                  <TableCell align="center">Total</TableCell>
                  <TableCell align="center">Completed</TableCell>
                  <TableCell align="center">In Progress</TableCell>
                  <TableCell align="center">Pending</TableCell>
                  <TableCell align="center">Avg Rating</TableCell>
                  <TableCell align="center">Completion Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {defectSummary.map((defect, index) => (
                  <TableRow key={index}>
                    <TableCell>{defect.defect_type_name}</TableCell>
                    <TableCell align="center">{defect.work_order_count}</TableCell>
                    <TableCell align="center">{defect.completed_count}</TableCell>
                    <TableCell align="center">{defect.in_progress_count}</TableCell>
                    <TableCell align="center">{defect.pending_count}</TableCell>
                    <TableCell align="center">
                      {defect.avg_quality_rating ? (
                        <Rating value={defect.avg_quality_rating} readOnly size="small" />
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={(defect.completed_count / defect.work_order_count) * 100}
                          sx={{ width: 60 }}
                        />
                        <Typography variant="body2">
                          {((defect.completed_count / defect.work_order_count) * 100).toFixed(0)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AnalyticsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Work Order Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive analytics and performance insights
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="365">Last year</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Overview Cards */}
      {renderOverviewCards()}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Overview" />
          <Tab label="Performance" />
          <Tab label="Defect Analysis" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {renderStatusDistribution()}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderPriorityDistribution()}
            </Grid>
            <Grid item xs={12}>
              {renderTrendsChart()}
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {renderTopPerformers()}
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {renderDefectTypeSummary()}
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default WorkOrderAnalyticsDashboard; 